import { CopyOutlined, ReloadOutlined } from "@ant-design/icons";
import { Alert, Button, Input, Popconfirm, Spin } from "antd";
import queryClient from "api/queryClient.ts";
import {
    useGetConversionApiToken,
    useGetConversionApiTokenForUser,
    useGetTenantUserRegistrationFormQuery,
    useRegenerateConversionApiTokenMutation,
    useRegenerateConversionApiTokenForUserMutation,
    useSetUserCommissionGroupsMutation,
    useStoreTenantUserMutation,
    useUpdateTenantUserRegistrationFormMutation,
} from "api/TenantSlug/User/hooks.ts";
import { useGetRegistrationStructureQuery } from "api/TenantSlug/UserStructure/hooks.ts";
import { useUpdateUserMutation } from "api/User/hooks.ts";
import { AxiosError } from "axios";
import {
    accountGlobalFieldRows,
    AccountGlobalFieldSection,
} from "components/AccountSettings/AccountGlobalFieldsSection.tsx";
import { getTenantRegistrationStatus } from "components/Forms/TenantRegistrationFlow/registrationFlow.ts";
import { useTenantCheckEmail } from "components/Forms/TenantRegistrationFlow/useTenantCheckEmail.ts";
import {
    buildTenantUserFormDefaults,
    buildTenantUserFormSchema,
    TenantUserFormData,
} from "components/Forms/TenantUserForm/formSchema.ts";
import { RuntimeFormFieldCell } from "components/Forms/TenantUserForm/RuntimeFormFieldCell.tsx";
import { DrawerCollapsible } from "components/UI/DrawerCollapsible/DrawerCollapsible.tsx";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider.tsx";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm.ts";
import { PropertiesList } from "components/UI/PropertiesList/PropertiesList.tsx";
import { CommissionGroupSelectSearchable } from "components/UI/Selects/CommissionGroupSelectSearchable/CommissionGroupSelectSearchable.tsx";
import { InteractiveCell } from "components/UI/Table/UI/InteractiveCell/InteractiveCell.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { usePermission } from "hooks/usePermissions.ts";
import { useUser } from "hooks/useUser.ts";
import { COMMISSION_GROUP_USER_GROUP, TOAST_TYPES } from "localConstants.ts";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useWatch } from "react-hook-form";
import { toast } from "react-toastify";
import { CheckEmailStatus, PermissionEnum, RoleEnum } from "types/enums.ts";
import { ApiErrorResponse, FormId } from "types/models.ts";
import { extractValidationErrorMessages } from "utils/extractValidationErrorMessages.ts";
import { showCustomToast } from "utils/toast.ts";

import FormFieldData = App.Data.FormFieldData;

const EMAIL_CHECK_DEBOUNCE_MS = 600;

interface TenantUserFormProps {
    role: RoleEnum.AffiliatePartner | RoleEnum.Advertiser;
    mode: "create" | "detail";
    user?: App.Data.UserData;
    onSuccess?: () => void;
    onCanSubmitChange?: (canSubmit: boolean) => void;
}

const structureName = (role: TenantUserFormProps["role"]) =>
    role === RoleEnum.AffiliatePartner ? "registration-affiliate-partner" : "registration-advertiser";

const rolePath = (role: TenantUserFormProps["role"]) =>
    role === RoleEnum.AffiliatePartner ? "affiliate" : "advertiser";

const sectionNames = Object.keys(accountGlobalFieldRows) as AccountGlobalFieldSection[];
const sectionSystemNames = Object.fromEntries(
    sectionNames.map((section) => [section, new Set(accountGlobalFieldRows[section].flat())])
) as Record<AccountGlobalFieldSection, Set<string>>;

const isAccountField = (field: FormFieldData, section: AccountGlobalFieldSection) =>
    field.global_form_field_id !== null && sectionSystemNames[section].has(field.system_name);

const fieldsForSection = (fields: FormFieldData[], section: AccountGlobalFieldSection) =>
    fields.filter((field) => !field.is_hidden && isAccountField(field, section));

const otherFields = (fields: FormFieldData[]) =>
    fields.filter(
        (field) =>
            !field.is_hidden &&
            !["email", "nickname"].includes(field.system_name) &&
            !sectionNames.some((section) => isAccountField(field, section))
    );

const FieldRows: FC<{
    fields: FormFieldData[];
    mode: TenantUserFormProps["mode"];
}> = ({ fields, mode }) => (
    <PropertiesList
        list={fields.map((field) => ({
            term: (
                <>
                    {field.visible_description}
                    {field.is_required && <span className="ml-1 text-[#b91c1c]">*</span>}
                </>
            ),
            render: (
                <RuntimeFormFieldCell
                    field={field}
                    alwaysEditing={mode === "create"}
                    editable={mode === "create" || field.global_form_field_id === null}
                />
            ),
        }))}
    />
);

const AccountInputs: FC<{
    mode: TenantUserFormProps["mode"];
    emailLocked: boolean;
    showNickname: boolean;
    onEmailChange: () => void;
}> = ({ mode, emailLocked, showNickname, onEmailChange }) => {
    const { control } = useGeneralForm<TenantUserFormData>();
    const { t: tFields } = usePaldockTranslation("forms.general-fields");

    return (
        <PropertiesList
            list={[
                {
                    term: tFields("email.label"),
                    render: (
                        <Controller
                            control={control}
                            name="email"
                            render={({ field, fieldState }) =>
                                mode === "detail" ? (
                                    <span>{field.value || "-"}</span>
                                ) : (
                                    <Input
                                        {...field}
                                        type="email"
                                        disabled={emailLocked}
                                        status={fieldState.error ? "error" : undefined}
                                        onChange={(event) => {
                                            field.onChange(event);
                                            onEmailChange();
                                        }}
                                    />
                                )
                            }
                        />
                    ),
                },
                ...(showNickname
                    ? [
                          {
                              term: tFields("nickname.label"),
                              render: (
                                  mode === "detail" ? (
                                      <InteractiveCell.Input
                                          formControl={{ control, name: "nickname" }}
                                          placeholder={tFields("nickname.placeholder")}
                                      />
                                  ) : (
                                      <Controller
                                          control={control}
                                          name="nickname"
                                          render={({ field, fieldState }) => (
                                              <Input
                                                  {...field}
                                                  disabled={emailLocked}
                                                  status={fieldState.error ? "error" : undefined}
                                              />
                                          )}
                                      />
                                  )
                              ),
                          },
                      ]
                    : []),
            ]}
        />
    );
};

const PasswordRows: FC = () => {
    const { control } = useGeneralForm<TenantUserFormData>();
    const { t: tFields } = usePaldockTranslation("forms.general-fields");

    return (
        <PropertiesList
            list={[
                {
                    term: tFields("password.label"),
                    render: (
                        <Controller
                            control={control}
                            name="password"
                            render={({ field, fieldState }) => (
                                <Input.Password {...field} status={fieldState.error ? "error" : undefined} />
                            )}
                        />
                    ),
                },
                {
                    term: tFields("password_confirmation.label"),
                    render: (
                        <Controller
                            control={control}
                            name="password_confirmation"
                            render={({ field, fieldState }) => (
                                <Input.Password {...field} status={fieldState.error ? "error" : undefined} />
                            )}
                        />
                    ),
                },
            ]}
        />
    );
};

const TenantUserSettings: FC<{
    mode: TenantUserFormProps["mode"];
    user?: App.Data.UserData;
}> = ({ mode, user }) => {
    const { control } = useGeneralForm<TenantUserFormData>();
    const permissions = usePermission();
    const currentUser = useUser().getAuthenticatedUser();
    const isSelf = currentUser?.id === user?.id;
    const canSetCommissionGroup = permissions.hasPermission(PermissionEnum.TenantUserSetCommissionGroups);
    const canViewToken =
        mode === "detail" &&
        permissions.hasPermission(isSelf ? PermissionEnum.OfferApiViewOwn : PermissionEnum.OfferApiViewOthers);
    const canRegenerateToken = permissions.hasPermission(
        isSelf ? PermissionEnum.OfferApiRegenerateOwnToken : PermissionEnum.OfferApiRegenerateForeignToken
    );
    const ownTokenQuery = useGetConversionApiToken({ enabled: canViewToken && isSelf });
    const foreignTokenQuery = useGetConversionApiTokenForUser(user?.id, {
        enabled: canViewToken && !isSelf && !!user?.id,
    });
    const tokenQuery = isSelf ? ownTokenQuery : foreignTokenQuery;
    const regenerateOwnToken = useRegenerateConversionApiTokenMutation({
        onSuccess: () => void ownTokenQuery.refetch(),
    });
    const regenerateForeignToken = useRegenerateConversionApiTokenForUserMutation({
        onSuccess: () => void foreignTokenQuery.refetch(),
    });
    const { t: tFields } = usePaldockTranslation("forms.general-fields");
    const { t: tCommon } = usePaldockTranslation("common");
    const { t: tUserForm } = usePaldockTranslation("forms.tenant-user");

    const list = [];
    if (canSetCommissionGroup) {
        list.push({
            term: tFields("commission_group.label"),
            render: (
                <Controller
                    control={control}
                    name="commission_group_id"
                    render={({ field }) => (
                        <CommissionGroupSelectSearchable {...field} withDefaultUserGroup className="w-full" />
                    )}
                />
            ),
        });
    }
    if (canViewToken) {
        list.push({
            term: tUserForm("fields.conversion_api_token"),
            render: (
                <div className="flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate">{tokenQuery.data?.token ?? "-"}</span>
                    <Button
                        type="text"
                        icon={<CopyOutlined />}
                        disabled={!tokenQuery.data?.token}
                        onClick={() => void navigator.clipboard.writeText(tokenQuery.data?.token ?? "")}
                    >
                        {tCommon("copy")}
                    </Button>
                    {canRegenerateToken && user?.id && (
                        <Popconfirm
                            title={tUserForm("messages.regenerate_token_confirm")}
                            onConfirm={() =>
                                isSelf ? regenerateOwnToken.mutate() : regenerateForeignToken.mutate(user.id)
                            }
                        >
                            <Button
                                type="text"
                                icon={<ReloadOutlined />}
                                loading={regenerateOwnToken.isPending || regenerateForeignToken.isPending}
                            >
                                {tUserForm("actions.regenerate")}
                            </Button>
                        </Popconfirm>
                    )}
                </div>
            ),
        });
    }

    return list.length > 0 ? (
        <DrawerCollapsible title={tCommon("settings")}>
            <PropertiesList list={list} />
        </DrawerCollapsible>
    ) : null;
};

const TenantUserFormContent: FC<
    TenantUserFormProps & {
        fields: FormFieldData[];
        checkedStatus: CheckEmailStatus | "idle";
        isCheckingEmail: boolean;
        checkedNickname: string | null;
        onCheckEmail: (email: string) => void;
        onResetEmail: () => void;
        onDirtyFieldsChange: (fieldNames: string[], commissionGroupDirty: boolean) => void;
    }
> = ({
    mode,
    user,
    fields,
    checkedStatus,
    isCheckingEmail,
    checkedNickname,
    onCheckEmail,
    onResetEmail,
    onCanSubmitChange,
    onDirtyFieldsChange,
}) => {
    const { control, formState, setValue } = useGeneralForm<TenantUserFormData>();
    const { t } = usePaldockTranslation("forms.tenant-user");
    const email = useWatch({ control, name: "email" });
    const isCreate = mode === "create";
    const { isExistingAccount: isExisting } = getTenantRegistrationStatus(checkedStatus);
    const showForm = !isCreate || checkedStatus === CheckEmailStatus.New;
    const personalFields = fieldsForSection(fields, "personal");
    const contactFields = fieldsForSection(fields, "contact");
    const companyFields = fieldsForSection(fields, "company");
    const customFields = otherFields(fields);

    useEffect(() => {
        if (!isCreate || checkedStatus !== "idle" || !email || formState.errors.email) return;
        const timer = setTimeout(() => onCheckEmail(email), EMAIL_CHECK_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [checkedStatus, email, formState.errors.email, isCreate, onCheckEmail]);

    useEffect(() => {
        if (isExisting && checkedNickname) setValue("nickname", checkedNickname);
    }, [checkedNickname, isExisting, setValue]);

    useEffect(() => {
        onCanSubmitChange?.(mode === "detail" || checkedStatus === CheckEmailStatus.New);
    }, [checkedStatus, mode, onCanSubmitChange]);

    useEffect(() => {
        const dirtyFieldMap = formState.dirtyFields.fields as Record<string, boolean> | undefined;
        onDirtyFieldsChange(
            Object.entries(dirtyFieldMap ?? {})
                .filter(([, dirty]) => dirty)
                .map(([systemName]) => systemName),
            Boolean(formState.dirtyFields.commission_group_id)
        );
    }, [formState.dirtyFields, onDirtyFieldsChange]);

    const statusMessage = (() => {
        if (checkedStatus === CheckEmailStatus.ExistingSameRole) return t("messages.existing_same_role");
        if (checkedStatus === CheckEmailStatus.ExistingPendingRole) return t("messages.existing_pending_role");
        if (checkedStatus === CheckEmailStatus.ExistingDifferentRole) return t("messages.existing_different_role_todo");
        if (checkedStatus === CheckEmailStatus.ExistingNoAccess) return t("messages.existing_invitation_todo");
        return null;
    })();

    return (
        <div className="space-y-3">
            <DrawerCollapsible title={t("sections.personal_data")}>
                <AccountInputs
                    mode={mode}
                    emailLocked={isExisting}
                    showNickname={!isCreate || checkedStatus !== "idle"}
                    onEmailChange={onResetEmail}
                />
                {isCheckingEmail && <Spin className="m-3" size="small" />}
                {statusMessage && <Alert className="m-3" type="warning" showIcon message={statusMessage} />}
                {showForm && isCreate && <PasswordRows />}
                {showForm && personalFields.length > 0 && <FieldRows fields={personalFields} mode={mode} />}
            </DrawerCollapsible>
            {showForm && <TenantUserSettings mode={mode} user={user} />}
            {showForm && contactFields.length > 0 && (
                <DrawerCollapsible title={t("sections.contact_data")}>
                    <FieldRows fields={contactFields} mode={mode} />
                </DrawerCollapsible>
            )}
            {showForm && companyFields.length > 0 && (
                <DrawerCollapsible title={t("sections.company_data")}>
                    <FieldRows fields={companyFields} mode={mode} />
                </DrawerCollapsible>
            )}
            {showForm && customFields.length > 0 && (
                <DrawerCollapsible title={t("sections.custom_fields")}>
                    <FieldRows fields={customFields} mode={mode} />
                </DrawerCollapsible>
            )}
        </div>
    );
};

export const TenantUserForm: FC<TenantUserFormProps> = (props) => {
    const { role, mode, user, onSuccess } = props;
    const isCreate = mode === "create";
    const tenant = useUser().getActiveTenant();
    const permissions = usePermission();
    const registrationStructureQuery = useGetRegistrationStructureQuery(structureName(role), { enabled: isCreate });
    const detailFormQuery = useGetTenantUserRegistrationFormQuery(user?.id, role, { enabled: !isCreate && !!user?.id });
    const fields = useMemo(
        () =>
            [
                ...((isCreate
                    ? registrationStructureQuery.data?.formStructure.formFields
                    : detailFormQuery.data?.formStructure.formFields) ?? []),
            ].sort((a, b) => a.weight - b.weight),
        [detailFormQuery.data, isCreate, registrationStructureQuery.data]
    );
    const values = isCreate ? undefined : (detailFormQuery.data?.values ?? {});
    const currentCommissionGroupId = user?.commissionGroups?.[0]?.id ?? COMMISSION_GROUP_USER_GROUP;
    const defaults = useMemo(
        () => buildTenantUserFormDefaults(fields, values, user, currentCommissionGroupId),
        [currentCommissionGroupId, fields, user, values]
    );
    const schema = useMemo(() => buildTenantUserFormSchema(fields, mode), [fields, mode]);
    const { status, nickname, isCheckingEmail, checkEmail, resetStatus } = useTenantCheckEmail(tenant?.id ?? "");
    const storeUser = useStoreTenantUserMutation();
    const updateUser = useUpdateUserMutation();
    const updateFields = useUpdateTenantUserRegistrationFormMutation(user?.id ?? "", role);
    const setCommissionGroup = useSetUserCommissionGroupsMutation(user?.id ?? "");
    const canSetCommissionGroup = permissions.hasPermission(PermissionEnum.TenantUserSetCommissionGroups);
    const { t } = usePaldockTranslation(`forms.${rolePath(role)}-add`);
    const { t: tUserForm } = usePaldockTranslation("forms.tenant-user");
    const dirtyFieldNames = useRef<string[]>([]);
    const commissionGroupDirty = useRef(false);
    const [savedVersion, setSavedVersion] = useState(0);

    const handleSubmit = async (data: TenantUserFormData) => {
        try {
            if (isCreate) {
                if (status !== CheckEmailStatus.New) return;
                const response = await storeUser.mutateAsync({
                    email: data.email,
                    nickname: data.nickname,
                    password: data.password,
                    password_confirmation: data.password_confirmation,
                    role,
                    fields: data.fields,
                    ...(canSetCommissionGroup && data.commission_group_id !== COMMISSION_GROUP_USER_GROUP
                        ? { commission_group_ids: [data.commission_group_id] }
                        : {}),
                });
                showCustomToast({ title: t("messages.success") }, TOAST_TYPES.SUCCESS);
                await queryClient.invalidateQueries({ queryKey: ["getUsers"] });
                if (response.user.id) onSuccess?.();
                return;
            }

            if (!user?.id) return;
            if (data.nickname !== (user.nickname ?? "")) {
                await updateUser.mutateAsync({
                    userId: user.id,
                    request: { nickname: data.nickname },
                });
            }
            const dirtyFields = Object.fromEntries(
                dirtyFieldNames.current.map((systemName) => [systemName, data.fields[systemName]])
            );
            if (dirtyFieldNames.current.length > 0) {
                await updateFields.mutateAsync({ fields: dirtyFields });
            }
            if (canSetCommissionGroup && commissionGroupDirty.current) {
                await setCommissionGroup.mutateAsync({
                    commission_group_ids:
                        data.commission_group_id === COMMISSION_GROUP_USER_GROUP ? [] : [data.commission_group_id],
                });
            }
            await Promise.all([
                detailFormQuery.refetch(),
                queryClient.invalidateQueries({ queryKey: ["getUser", user.id] }),
                queryClient.invalidateQueries({ queryKey: ["getCommissionGroups"] }),
            ]);
            setSavedVersion((version) => version + 1);
            toast.success(tUserForm("messages.saved"));
        } catch (error) {
            const messages = extractValidationErrorMessages(error as AxiosError<ApiErrorResponse>);
            toast.error(messages[0] ?? tUserForm("messages.save_error"));
        }
    };

    if ((isCreate && registrationStructureQuery.isLoading) || (!isCreate && (!user || detailFormQuery.isLoading))) {
        return <Spin className="flex justify-center p-8" />;
    }

    if (registrationStructureQuery.isError || detailFormQuery.isError) {
        return <Alert type="error" showIcon message={tUserForm("messages.form_unavailable")} />;
    }

    return (
        <GeneralFormProvider<TenantUserFormData>
            key={`${mode}-${user?.id ?? "new"}-${fields.length}-${savedVersion}`}
            id={FormId.AddUserForm}
            schema={schema}
            defaultValues={defaults}
            onSubmit={handleSubmit}
            resetOnSubmit={false}
        >
            <TenantUserFormContent
                {...props}
                fields={fields}
                checkedStatus={isCreate ? status : CheckEmailStatus.New}
                checkedNickname={nickname}
                isCheckingEmail={isCheckingEmail}
                onCheckEmail={(email) => checkEmail(email, role)}
                onResetEmail={resetStatus}
                onDirtyFieldsChange={(fieldNames, isCommissionGroupDirty) => {
                    dirtyFieldNames.current = fieldNames;
                    commissionGroupDirty.current = isCommissionGroupDirty;
                }}
            />
        </GeneralFormProvider>
    );
};
